import { useDispatch } from 'react-redux'
import { AppDispatch } from '../store'
import { RESOURCE_CHANGE_SUCCESS } from '../store/user/action-types'

/**
 * Custom hook for syncing resources from API responses to Redux state
 * 
 * This hook provides a centralized way to update user resources in Redux
 * after API calls that modify resources (swaps, upgrades, repairs, etc.)
 * 
 * @returns Object with syncResources function
 * 
 * @example
 * ```tsx
 * const { syncResources } = useResourceSync()
 * 
 * // After an API call that returns updated resources
 * const response = await someApiCall()
 * syncResources(response)
 * ```
 */
export const useResourceSync = () => {
  const dispatch = useDispatch<AppDispatch>()

  /**
   * Syncs resources from API response to Redux state
   * 
   * @param data - API response data containing resource updates
   *                Can be either:
   *                - Direct resource object: { ecs, iron, wood, stone, bread, wheat, water, ... }
   *                - Nested user object: { user: { ecs, iron, ... } }
   *                - Response with success flag: { success: true, ecs, iron, ... }
   * 
   * @returns boolean - true if resources were synced, false otherwise
   */
  const syncResources = (data: any): boolean => {
    if (!data) {
      return false
    }

    // Skip if the response indicates failure
    if (data.success === false) {
      return false
    }

    // Check if data contains any resource fields
    const hasResources = 
      data.ecs !== undefined ||
      data.iron !== undefined ||
      data.wood !== undefined ||
      data.stone !== undefined ||
      data.bread !== undefined ||
      data.wheat !== undefined ||
      data.water !== undefined ||
      data.user?.ecs !== undefined ||
      data.user?.iron !== undefined ||
      data.user?.wood !== undefined ||
      data.user?.stone !== undefined ||
      data.user?.bread !== undefined ||
      data.user?.wheat !== undefined ||
      data.user?.water !== undefined

    if (!hasResources) {
      return false
    }

    // Dispatch resource update to Redux
    dispatch({
      type: RESOURCE_CHANGE_SUCCESS,
      payload: { data: data }
    })

    return true
  }

  return { syncResources }
}

/**
 * Standalone function version for use in Redux actions (outside React components)
 * 
 * @param dispatch - Redux dispatch function
 * @param data - API response data containing resource updates
 * 
 * @returns boolean - true if resources were synced, false otherwise
 * 
 * @example
 * ```ts
 * export const someAction = (walletAddress: string) => async (dispatch: AppDispatch) => {
 *   const data = await someApiCall(walletAddress)
 *   syncResourcesFromResponse(dispatch, data)
 * }
 * ```
 */
export const syncResourcesFromResponse = (dispatch: AppDispatch, data: any): boolean => {
  if (!data) {
    return false
  }

  // Skip if the response indicates failure
  if (data.success === false) {
    return false
  }

  // Check if data contains any resource fields
  const hasResources = 
    data.ecs !== undefined ||
    data.iron !== undefined ||
    data.wood !== undefined ||
    data.stone !== undefined ||
    data.bread !== undefined ||
    data.wheat !== undefined ||
    data.water !== undefined ||
    data.user?.ecs !== undefined ||
    data.user?.iron !== undefined ||
    data.user?.wood !== undefined ||
    data.user?.stone !== undefined ||
    data.user?.bread !== undefined ||
    data.user?.wheat !== undefined ||
    data.user?.water !== undefined

  if (!hasResources) {
    return false
  }

  // Dispatch resource update to Redux
  dispatch({
    type: RESOURCE_CHANGE_SUCCESS,
    payload: { data: data }
  })

  return true
}
